'use client';

import React, { useState, useEffect, useCallback } from 'react';

// ─── PHOTOGRAPH DATA ──────────────────────────────────────────────────────────
// 27 photographs from the Monolyth (VR) Behance gallery
// Images sourced from mir-s3-cdn-cf.behance.net CDN
// Each image has a description panel for the lightbox

interface PhotographItem {
  id: number;
  src: string;
  alt: string;
  title: string;
  description: string;
  location: string;
  year: string;
  category: 'pittsburgh' | 'detroit' | 'chicago' | 'concept' | 'process';
  bridgeLinks: {label: string;href: string;color: string;}[];
}

const PHOTOGRAPHS: PhotographItem[] = [
{
  id: 1,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/1a3a92112291665.67fbabb8975ac.jpg',
  alt: 'Monolyth VR environment — industrial corridor with surreal atmospheric haze over Pittsburgh steel mill ruins',
  title: 'Pittsburgh · Industrial Corridor I',
  description: 'Environmental research documentation of the Braddock, PA industrial corridor. The steel mill ruins form the architectural backbone of the first Monolyth VR environment — a landscape where industrial erosion and community resilience coexist.',
  location: 'Pittsburgh / Braddock, PA',
  year: '2018',
  category: 'pittsburgh',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 2,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/060d80112291665.67fbabb895bef.jpg',
  alt: 'Monolyth VR concept environment — layered digital landscape with glitch artifacts over urban decay',
  title: 'Pittsburgh · Industrial Corridor II',
  description: 'Second documentation frame from the Pittsburgh research phase. The layering of physical decay with digital overlay begins to emerge — the visual language that would define the Monolyth open-world environments.',
  location: 'Pittsburgh / Braddock, PA',
  year: '2018',
  category: 'pittsburgh',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' }]

},
{
  id: 3,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/c91c58112291665.67fbabb898793.jpg',
  alt: 'Monolyth VR environment still — surreal landscape with environmental data glyphs embedded in terrain',
  title: 'Pittsburgh · Glyph Environment I',
  description: 'The glyph system begins to emerge — street map data from Braddock encoded as environmental textures. Each glyph carries embedded community data, the first iteration of what would become the Keys to Kulli Haal curriculum.',
  location: 'Pittsburgh / Braddock, PA',
  year: '2018',
  category: 'pittsburgh',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'SL Glossary', href: '/sl-glossary', color: '#c9a84c' }]

},
{
  id: 4,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/298748112291665.67fbabb895592.jpg',
  alt: 'Monolyth VR base image — Pittsburgh community space with atmospheric environmental processing',
  title: 'Pittsburgh · Community Space I',
  description: 'Community space documentation — the human scale within the industrial landscape. These spaces of gathering and resilience became the cultural centers of the Monolyth virtual environments.',
  location: 'Pittsburgh / Braddock, PA',
  year: '2018',
  category: 'pittsburgh',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 5,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/4489ce112291665.67fbabb894f76.jpg',
  alt: 'Monolyth VR environment — Pittsburgh steel infrastructure with digital surreal overlay and color grading',
  title: 'Pittsburgh · Steel Infrastructure',
  description: 'The steel infrastructure of Braddock — once the engine of American industry, now a monument to structural violence against communities of color. The Monolyth project seeks to reclaim these spaces through virtual cultural memory.',
  location: 'Pittsburgh / Braddock, PA',
  year: '2018',
  category: 'pittsburgh',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 6,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/321ddf112291665.67fbabb894019.jpg',
  alt: 'Monolyth VR process image — Pittsburgh landscape with glyph overlay and environmental data visualization',
  title: 'Pittsburgh · Process Documentation I',
  description: 'Process documentation — the translation of physical space into digital environment. The glyph overlay begins to encode the community data that will be embedded in each virtual landscape.',
  location: 'Pittsburgh / Braddock, PA',
  year: '2018',
  category: 'process',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' }]

},
{
  id: 7,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/019f2a112291665.67fbabb89bfc6.jpg',
  alt: 'Monolyth VR environment still — Detroit urban landscape with atmospheric processing and surreal color palette',
  title: 'Detroit · Urban Landscape I',
  description: 'The Detroit research phase begins — a city whose story of industrial decline and Black community resilience mirrors Pittsburgh in scale and depth. The visual language shifts to reflect Detroit\'s distinct architectural character.',
  location: 'Detroit, MI',
  year: '2019',
  category: 'detroit',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 8,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/7221d0112291665.67fbabb8981ad.jpg',
  alt: 'Monolyth VR Detroit environment — abandoned industrial space with community murals and digital glyph overlay',
  title: 'Detroit · Community Documentation I',
  description: 'Community documentation in Detroit — murals, gathering spaces, and cultural markers that persist despite industrial abandonment. These become the artifacts that populate the Monolyth virtual cultural center.',
  location: 'Detroit, MI',
  year: '2019',
  category: 'detroit',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 9,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/0b1946112291665.67fbabb89c4a7.jpg',
  alt: 'Monolyth VR Detroit environment still — industrial corridor with atmospheric haze and environmental data',
  title: 'Detroit · Industrial Corridor',
  description: 'Detroit\'s industrial corridors — the physical evidence of environmental racism encoded in the landscape. The Monolyth project documents these spaces as a form of counter-archiving against erasure.',
  location: 'Detroit, MI',
  year: '2019',
  category: 'detroit',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 10,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/5c501b112291665.67fbabb898d9d.jpg',
  alt: 'Monolyth VR Detroit glyph environment — street map data encoded as surreal terrain with cultural markers',
  title: 'Detroit · Glyph Environment',
  description: 'The Detroit glyph environment — street map data from Detroit\'s historically Black neighborhoods encoded as terrain. Each glyph carries the weight of community history, displacement, and resilience.',
  location: 'Detroit, MI',
  year: '2019',
  category: 'detroit',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'SL Glossary', href: '/sl-glossary', color: '#c9a84c' }]

},
{
  id: 11,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/f1cfa3112291665.67fbabb8994f6.jpg',
  alt: 'Monolyth VR Detroit process documentation — environmental research with digital overlay and color processing',
  title: 'Detroit · Process Documentation',
  description: 'Process documentation from the Detroit research phase — the translation methodology that converts physical community spaces into virtual environments. The process itself becomes a form of caretaking.',
  location: 'Detroit, MI',
  year: '2019',
  category: 'process',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 12,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/3be73c112291665.67fbabb893aee.jpg',
  alt: 'Monolyth VR Chicago environment — South Side landscape with atmospheric processing and glyph overlay',
  title: 'Chicago · South Side I',
  description: 'The Chicago research phase — South Side and West Side communities whose stories of environmental racism and cultural resilience complete the Monolyth trilogy. Chicago\'s scale and density create a distinct visual language.',
  location: 'Chicago, IL — South Side',
  year: '2020',
  category: 'chicago',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 13,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/eaabb9112291665.67fbabb896130.jpg',
  alt: 'Monolyth VR Chicago environment — West Side community space with digital surreal overlay',
  title: 'Chicago · West Side',
  description: 'West Side Chicago documentation — community spaces, cultural centers, and gathering places that persist as anchors of identity in the face of industrial displacement and gentrification.',
  location: 'Chicago, IL — West Side',
  year: '2020',
  category: 'chicago',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 14,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/be1fc5112291665.67fbabb894861.jpg',
  alt: 'Monolyth VR Chicago glyph environment — street map data encoded as surreal terrain with cultural markers',
  title: 'Chicago · Glyph Environment I',
  description: 'Chicago glyph environment — the most complex of the three cities, reflecting Chicago\'s layered history of Black Muslim community building, from the Nation of Islam to MANA\'s current organizing work.',
  location: 'Chicago, IL',
  year: '2020',
  category: 'chicago',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 15,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/1dc877112291665.67fbabb897c88.jpg',
  alt: 'Monolyth VR Chicago environment still — industrial landscape with atmospheric processing and community data',
  title: 'Chicago · Industrial Landscape',
  description: 'Chicago\'s industrial landscape — the physical evidence of decades of environmental racism encoded in the built environment. The Monolyth project documents these spaces as a form of counter-archiving.',
  location: 'Chicago, IL',
  year: '2020',
  category: 'chicago',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' }]

},
{
  id: 16,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/49be5d112291665.67fbabb893411.jpg',
  alt: 'Monolyth VR concept environment — surreal open world landscape with layered atmospheric effects',
  title: 'Concept Environment I',
  description: 'Concept environment development — the open-world VR space begins to take shape. The surreal landscapes are designed to feel simultaneously familiar and otherworldly, reflecting the three-reality system at the heart of Monolyth.',
  location: 'Virtual Environment',
  year: '2020',
  category: 'concept',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 17,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/e04fdc112291665.67fbabb89686f.jpg',
  alt: 'Monolyth VR concept environment — digital landscape with glyph system and environmental data visualization',
  title: 'Concept Environment II',
  description: 'Second concept environment — the glyph system fully integrated into the virtual landscape. The glyphs function as both aesthetic elements and data carriers, encoding community information in the terrain itself.',
  location: 'Virtual Environment',
  year: '2020',
  category: 'concept',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'SL Glossary', href: '/sl-glossary', color: '#c9a84c' }]

},
{
  id: 18,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/17fc5d112291665.67fbabb89b477.jpg',
  alt: 'Monolyth VR concept environment — atmospheric open world with layered reality system visualization',
  title: 'Concept Environment III',
  description: 'Third concept environment — the three-layer reality system fully visualized. Physical, digital, and spiritual dimensions coexist in the same space, each layer carrying different forms of community knowledge.',
  location: 'Virtual Environment',
  year: '2021',
  category: 'concept',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 19,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/eea906112291665.67fbabb89ad27.jpg',
  alt: 'Monolyth VR environment still — surreal landscape with cultural artifacts and community data embedded',
  title: 'Cultural Center Concept I',
  description: 'Cultural center concept — the virtual museum space where community artifacts are displayed. Each artifact is created by individuals from the communities represented in the street map glyphs.',
  location: 'Virtual Cultural Center',
  year: '2021',
  category: 'concept',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 20,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/385d02112291665.67fbabb89cea3.jpg',
  alt: 'Monolyth VR environment — Diawara collaboration space with atmospheric processing and cultural markers',
  title: 'Diawara Collaboration',
  description: 'The Diawara collaboration — a partnership that brought additional cultural perspectives into the Monolyth project. The collaboration expanded the scope of community representation in the virtual environments.',
  location: 'Virtual Environment',
  year: '2021',
  category: 'concept',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' }]

},
{
  id: 21,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/e902af112291665.67fbabb899ac4.jpg',
  alt: 'Monolyth VR process documentation — glyph development and environmental research methodology',
  title: 'Process Documentation II',
  description: 'Process documentation — the glyph development methodology. Each glyph is derived from actual street map data, transformed through a visual language that encodes community history in geometric form.',
  location: 'Studio / Research',
  year: '2021',
  category: 'process',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' }]

},
{
  id: 22,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/ab9a02112291665.67fbabb896fb2.jpg',
  alt: 'Monolyth VR archive image — environmental research documentation with atmospheric processing',
  title: 'Archive · Environmental Research I',
  description: 'Archive documentation — the environmental research methodology that underpins the Monolyth project. Field research, community interviews, and spatial analysis combine to create the data layer of each virtual environment.',
  location: 'Field Research',
  year: '2021',
  category: 'process',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' }]

},
{
  id: 23,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/35e06a112291665.67fbabb89a0d3.jpg',
  alt: 'Monolyth VR archive image — community documentation with digital processing and glyph overlay',
  title: 'Archive · Community Documentation',
  description: 'Community documentation archive — the human stories embedded in each virtual environment. The Monolyth project is ultimately a platform for community voice, using VR as a medium for counter-narrative.',
  location: 'Field Research',
  year: '2021',
  category: 'process',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 24,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/deafdb112291665.67fbabb89ba17.jpg',
  alt: 'Monolyth VR concept still — surreal landscape with layered atmospheric effects and cultural data',
  title: 'Concept Environment IV',
  description: 'Fourth concept environment — the open-world game space at its most developed. The landscape carries the full weight of the research: environmental data, community history, and cultural artifacts in a single immersive space.',
  location: 'Virtual Environment',
  year: '2021',
  category: 'concept',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 25,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/d503ad112291665.67fbabb89a5ff.jpg',
  alt: 'Monolyth VR environment still — Pittsburgh Braddock landscape with surreal digital processing',
  title: 'Pittsburgh · Base Image II',
  description: 'Pittsburgh base image — the raw photographic documentation before digital processing. The base images serve as the foundation layer of each virtual environment, grounding the surreal in the real.',
  location: 'Pittsburgh / Braddock, PA',
  year: '2018',
  category: 'pittsburgh',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 26,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/0a4611112291665.67fbabb89dce1.jpg',
  alt: 'Monolyth VR environment still — Detroit landscape with atmospheric processing and community data overlay',
  title: 'Detroit · Base Image',
  description: 'Detroit base image — the photographic foundation of the Detroit virtual environment. The raw documentation captures the texture of community life that the digital processing will amplify and transform.',
  location: 'Detroit, MI',
  year: '2019',
  category: 'detroit',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' }]

},
{
  id: 27,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/9ec022112291665.67fbabb89c98b.jpg',
  alt: 'Monolyth VR Chicago South Side base image — community landscape with atmospheric processing',
  title: 'Chicago · South Side II',
  description: 'Chicago South Side base image — the final city in the Monolyth trilogy. The South Side\'s rich history of Black Muslim community building, from the Nation of Islam to contemporary organizing, is encoded in every frame.',
  location: 'Chicago, IL — South Side',
  year: '2020',
  category: 'chicago',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

}];


const CATEGORY_COLORS: Record<string, string> = {
  pittsburgh: '#c9a84c',
  detroit: '#2d8a87',
  chicago: '#8a8ab8',
  concept: '#D4A82A',
  process: 'rgba(212,197,169,0.5)'
};

const CATEGORY_LABELS: Record<string, string> = {
  pittsburgh: 'Pittsburgh · Braddock',
  detroit: 'Detroit',
  chicago: 'Chicago',
  concept: 'Concept Environments',
  process: 'Process Documentation'
};

// ─── LIGHTBOX ─────────────────────────────────────────────────────────────────

interface LightboxProps {
  photo: PhotographItem;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  currentIndex: number;
  total: number;
}

function Lightbox({ photo, onClose, onPrev, onNext, currentIndex, total }: LightboxProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  const catColor = CATEGORY_COLORS[photo.category] || '#c9a84c';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.97)',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
      }}
      onClick={onClose}>

      {/* ── MOBILE LAYOUT: image fills top, info scrolls below ── */}
      <style>{`
        @media (max-width: 768px) {
          .lb-desktop { display: none !important; }
          .lb-mobile { display: flex !important; }
          .lb-image-wrap { height: 55vw !important; min-height: 220px !important; max-height: 60vh !important; padding: 0 !important; }
          .lb-image-wrap img { max-height: 100% !important; width: 100% !important; object-fit: contain !important; }
          .lb-info-panel { width: 100% !important; border-left: none !important; border-top: 3px solid var(--cat-color, #c9a84c) !important; padding: 20px 16px 40px !important; }
          .lb-prev { left: 8px !important; top: calc(55vw / 2) !important; }
          .lb-next { right: 8px !important; top: calc(55vw / 2) !important; }
        }
        @media (min-width: 769px) {
          .lb-mobile { display: none !important; }
          .lb-desktop { display: flex !important; }
        }
      `}</style>

      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: 'fixed', top: 12, right: 12,
          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
          color: 'rgba(212,197,169,0.8)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10001
        }}>
        ✕</button>

      {/* Counter */}
      <div style={{
        position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
        fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em',
        zIndex: 10001, background: 'rgba(0,0,0,0.5)', padding: '3px 10px', borderRadius: 20,
      }}>
        {currentIndex + 1} / {total}
      </div>

      {/* Prev button */}
      <button
        className="lb-prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        style={{
          position: 'fixed', left: 16, top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '50%', width: 44, height: 44, cursor: 'pointer',
          color: 'rgba(212,197,169,0.7)', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10001
        }}>
        ‹</button>

      {/* Next button */}
      <button
        className="lb-next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        style={{
          position: 'fixed', right: 16, top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '50%', width: 44, height: 44, cursor: 'pointer',
          color: 'rgba(212,197,169,0.7)', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10001
        }}>
        ›</button>

      {/* ── DESKTOP: side-by-side ── */}
      <div
        className="lb-desktop"
        style={{ flex: 1, width: '100%', alignItems: 'stretch', minHeight: '100vh' }}
        onClick={(e) => e.stopPropagation()}>

        {/* Image */}
        <div className="lb-image-wrap" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 70px' }}>
          <img
            src={photo.src}
            alt={photo.alt}
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
        </div>

        {/* Description panel */}
        <div
          className="lb-info-panel"
          style={{
            width: 320, flexShrink: 0,
            background: 'rgba(6,13,18,0.98)',
            borderLeft: `3px solid ${catColor}`,
            padding: '60px 24px 24px',
            overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 20,
            // @ts-ignore
            '--cat-color': catColor,
          }}>
          <InfoPanelContent photo={photo} catColor={catColor} />
        </div>
      </div>

      {/* ── MOBILE: stacked ── */}
      <div
        className="lb-mobile"
        style={{ flexDirection: 'column', width: '100%', minHeight: '100vh' }}
        onClick={(e) => e.stopPropagation()}>

        {/* Full-width image at top */}
        <div className="lb-image-wrap" style={{
          width: '100%', background: '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          paddingTop: 48,
        }}>
          <img
            src={photo.src}
            alt={photo.alt}
            style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain', display: 'block' }} />
        </div>

        {/* Swipe hint */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 24, padding: '10px 0 6px',
          background: 'rgba(0,0,0,0.8)',
        }}>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} style={{ background: 'none', border: 'none', color: 'rgba(212,197,169,0.5)', fontSize: 22, cursor: 'pointer', padding: '4px 16px' }}>‹</button>
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em', alignSelf: 'center' }}>{currentIndex + 1} / {total}</span>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} style={{ background: 'none', border: 'none', color: 'rgba(212,197,169,0.5)', fontSize: 22, cursor: 'pointer', padding: '4px 16px' }}>›</button>
        </div>

        {/* Info panel below */}
        <div
          className="lb-info-panel"
          style={{
            width: '100%',
            background: 'rgba(6,13,18,0.99)',
            borderTop: `3px solid ${catColor}`,
            padding: '20px 16px 48px',
            display: 'flex', flexDirection: 'column', gap: 16,
          }}>
          <InfoPanelContent photo={photo} catColor={catColor} />
        </div>
      </div>
    </div>
  );
}

function InfoPanelContent({ photo, catColor }: { photo: PhotographItem; catColor: string }) {
  return (
    <>
      {/* Category badge */}
      <div>
        <span style={{
          fontFamily: 'monospace', fontSize: 7, padding: '3px 10px', borderRadius: 3,
          background: `${catColor}15`, border: `1px solid ${catColor}40`, color: catColor,
          letterSpacing: '.1em', textTransform: 'uppercase'
        }}>
          {CATEGORY_LABELS[photo.category]}
        </span>
      </div>

      {/* Title */}
      <div>
        <h3 style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#F5F0E8', fontWeight: 400, margin: '0 0 6px' }}>
          {photo.title}
        </h3>
        <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em' }}>
          {photo.location} · {photo.year}
        </div>
      </div>

      {/* Description */}
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.75)', lineHeight: 1.8, margin: 0 }}>
        {photo.description}
      </p>

      {/* Bridge links */}
      <div>
        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 10 }}>
          ◈ Bridges to Haal Project
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {photo.bridgeLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                padding: '7px 12px', borderRadius: 4, textDecoration: 'none',
                background: `${link.color}10`, border: `1px solid ${link.color}30`, color: link.color,
                display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s'
              }}>
              <span style={{ fontSize: 10 }}>→</span> {link.label}
            </a>
          )}
        </div>
      </div>

      {/* Keyboard hint — desktop only */}
      <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)', letterSpacing: '.06em', lineHeight: 1.8 }}>
          ← → Arrow keys to navigate<br />
          Esc to close
        </div>
      </div>
    </>
  );
}

// ─── PHOTOGRAPHS SECTION ──────────────────────────────────────────────────────

export default function PhotographsSection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const filteredPhotos = activeFilter === 'all' ?
  PHOTOGRAPHS :
  PHOTOGRAPHS.filter((p) => p.category === activeFilter);

  const openLightbox = useCallback((photo: PhotographItem) => {
    const idx = filteredPhotos.findIndex((p) => p.id === photo.id);
    setLightboxIndex(idx);
  }, [filteredPhotos]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const prevPhoto = useCallback(() => {
    setLightboxIndex((prev) => prev !== null ? (prev - 1 + filteredPhotos.length) % filteredPhotos.length : null);
  }, [filteredPhotos.length]);

  const nextPhoto = useCallback(() => {
    setLightboxIndex((prev) => prev !== null ? (prev + 1) % filteredPhotos.length : null);
  }, [filteredPhotos.length]);

  return (
    <section id="photographs" style={{ paddingBottom: 80 }}>
      {/* Section header */}
      <div style={{ padding: '60px 7.1% 40px', borderBottom: '1px solid rgba(201,168,76,0.1)' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 10 }}>
          ◈ Portfolio · Segment I
        </p>
        <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 42, color: '#F5F0E8', fontWeight: 400, marginBottom: 14, lineHeight: 1.1 }}>
          Photographs
        </h2>
        <p style={{ fontFamily: "'Georgia', serif", fontSize: 15, color: 'rgba(212,197,169,0.6)', lineHeight: 1.8, maxWidth: 680, marginBottom: 0 }}>
          A highlighted collection of photography from the Monolyth (VR) research phases — Pittsburgh/Braddock, Detroit, and Chicago. Environmental documentation, community spaces, and concept environments that form the visual foundation of the open-world VR platform.
        </p>
      </div>

      {/* Main collage image */}
      <div style={{ marginBottom: 0 }}>
        <img
          src="https://mir-s3-cdn-cf.behance.net/project_modules/1400_webp/98b88d112291665.67fbabc562369.jpg"
          alt="Monolyth VR — main collage image showing the full scope of the environmental research across Pittsburgh, Detroit, and Chicago"
          style={{ width: '100%', display: 'block', maxHeight: 600, objectFit: 'cover' }} />
        
        <div style={{ padding: '10px 7.1%', background: 'rgba(0,0,0,0.5)', borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em', textTransform: 'uppercase' }}>
            Monolyth (VR) · Main Collage · Environmental Research 2018–2021
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ padding: '28px 7.1% 0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.35)', letterSpacing: '.1em', textTransform: 'uppercase', marginRight: 4 }}>
          Filter:
        </span>
        {['all', 'pittsburgh', 'detroit', 'chicago', 'concept', 'process'].map((f) => {
          const color = f === 'all' ? '#c9a84c' : CATEGORY_COLORS[f];
          const label = f === 'all' ? 'All 27' : CATEGORY_LABELS[f];
          const count = f === 'all' ? PHOTOGRAPHS.length : PHOTOGRAPHS.filter((p) => p.category === f).length;
          return (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase',
                padding: '5px 12px', borderRadius: 4, cursor: 'pointer',
                border: `1px solid ${activeFilter === f ? color + '55' : 'rgba(255,255,255,0.08)'}`,
                background: activeFilter === f ? `${color}12` : 'transparent',
                color: activeFilter === f ? color : 'rgba(212,197,169,0.4)',
                transition: 'all 0.15s'
              }}>
              
              {label} ({count})
            </button>);

        })}
      </div>

      {/* 27-image grid */}
      <div style={{ padding: '24px 7.1% 0' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 4
        }}>
          {filteredPhotos.map((photo, idx) => {
            const catColor = CATEGORY_COLORS[photo.category] || '#c9a84c';
            return (
              <div
                key={photo.id}
                onClick={() => openLightbox(photo)}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  aspectRatio: '4/3',
                  background: 'rgba(6,13,18,0.8)'
                }}>
                
                <img
                  src={photo.src}
                  alt={photo.alt}
                  style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    display: 'block', transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.04)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'} />
                

                {/* Hover overlay */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(6,13,18,0.85) 0%, transparent 50%)',
                  opacity: 0, transition: 'opacity 0.2s',
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}>
                  
                  <div style={{ fontFamily: 'monospace', fontSize: 7, color: catColor, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 3 }}>
                    {CATEGORY_LABELS[photo.category]}
                  </div>
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(212,197,169,0.9)', lineHeight: 1.4 }}>
                    {photo.title}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', marginTop: 3 }}>
                    {photo.year} · Click to expand
                  </div>
                </div>

                {/* Index number */}
                <div style={{
                  position: 'absolute', top: 8, left: 8,
                  fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)',
                  background: 'rgba(6,13,18,0.7)', padding: '2px 6px', borderRadius: 2,
                  letterSpacing: '.06em'
                }}>
                  {String(idx + 1).padStart(2, '0')}
                </div>

                {/* Category dot */}
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  width: 6, height: 6, borderRadius: '50%',
                  background: catColor, opacity: 0.7
                }} />
              </div>);

          })}
        </div>

        {/* Grid caption */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.06em' }}>
            {filteredPhotos.length} photographs · Click any image to expand with description
          </span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) =>
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: CATEGORY_COLORS[key] }} />
                <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)', letterSpacing: '.04em' }}>{label}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null &&
      <Lightbox
        photo={filteredPhotos[lightboxIndex]}
        onClose={closeLightbox}
        onPrev={prevPhoto}
        onNext={nextPhoto}
        currentIndex={lightboxIndex}
        total={filteredPhotos.length} />

      }
    </section>);

}